import { BackendPaymentMethodEnum } from '@/types/api/orders.types';
import { OrgRoles, SiteRoles } from '@/types/api/roles.types';
import { isUsableDepositPercent, shareAmountFor } from '@/common/utils/vendor-payments.utils';
import { convertToISOWithTimezone } from '@/common/utils/date.utils';
import type { BulkOrderPlacementPayment } from '@/types/api/bulk-order-placement.types';

/**
 * How one resulting order is to be paid, as the Payment and delivery step (S-11) collects it.
 *
 * Keyed by vendor on the wizard store, because the server mints one order per vendor (DL-115) and
 * matches `paymentInformation` to its groups by `vendorId`.
 */
export interface BulkOrderVendorPayment {
  /** The wire casing, not `PaymentOptionsEnum`: the server's account rule compares `"Account"` exactly. */
  method: BackendPaymentMethodEnum | null;
  /**
   * COD only, and optional: it can be set on the payment request later (CPTO, 2026-09-25). An
   * account payment falls due on the vendor's terms and the server refuses a date on one.
   */
  dueDate: Date | null;
  /** Who settles the order's own payment. Pre-flight checks it against this order's set (DL-141). */
  assigneeId: string | null;
  deposit: BulkOrderDeposit;
}

export interface BulkOrderDeposit {
  isOn: boolean;
  /**
   * The value of record, whichever of the two cells the officer typed into: an amount converts to
   * this on the way in and renders back from it, as the standard Confirm step's deposit does.
   */
  percent: number | null;
  dueDate: Date | null;
  assigneeId: string | null;
}

export const emptyVendorPayment = (): BulkOrderVendorPayment => ({
  method: null,
  dueDate: null,
  assigneeId: null,
  deposit: { isOn: false, percent: null, dueDate: null, assigneeId: null },
});

/**
 * Who may be assigned a **bulk** order's payment: org finance roles only (FR-42).
 *
 * Context: deliberately not `PAYMENT_ASSIGNEE_ROLES`, which adds `SITE_FIN_OFF`. A bulk order is
 * settled by one site-decoupled Payment Request, so a site's finance officer has no standing on it
 * and pre-flight refuses one (DL-141).
 */
export const BULK_ORDER_ASSIGNEE_ROLES = [OrgRoles.ORG_ADMIN, OrgRoles.ORG_FO, OrgRoles.ORG_FA];

/**
 * Who may be assigned a **standard** order's payment, which is what a portion covering fewer than
 * two sites becomes (FR-4).
 *
 * Context: the platform's own order rule, which `OrderPlacementComposer` enforces and which refuses
 * an `ORG_FO` outright. FR-42 does not reach this order because it produces no bulk PR (DL-141), so
 * two blocks on one screen legitimately offer different people.
 */
export const STANDARD_ORDER_ASSIGNEE_ROLES = [OrgRoles.ORG_ADMIN, SiteRoles.SITE_FIN_OFF];

export interface DepositFigures {
  percent: number;
  amount: number;
  balance: number;
}

/**
 * What a deposit comes to against the order total.
 *
 * The amount is what placement sends (`toPlacementPayment`), and Review restates all three from
 * here rather than working them out a second time, so step 4, Review and the request agree.
 *
 * `null` for a deposit that is off, empty or out of range, so nothing renders a split that the
 * step is about to refuse.
 */
export const depositFigures = (
  deposit: BulkOrderDeposit,
  totalInclVat: number,
): DepositFigures | null => {
  if (!deposit.isOn || !isDepositPercentValid(deposit.percent)) return null;

  const percent = deposit.percent;
  const amount = shareAmountFor(totalInclVat, percent);

  return { percent, amount, balance: Math.round((totalInclVat - amount) * 100) / 100 };
};

/**
 * The standard flows' deposit rule (`isUsableDepositPercent`): 0.01% to 99.99%. Finer at either
 * end is not what is placed: 99.999% showed a one-cent balance and was stored as 100%, leaving the
 * order's own payment request at nothing.
 */
const isDepositPercentValid = isUsableDepositPercent;

export type PaymentProblem = 'method' | 'deposit';

/**
 * What still stops this order being placed, in the order the block renders its fields.
 *
 * One definition for the block's inline errors, the footer's count and the store's gate on Review,
 * so the three cannot disagree about whether an order is ready.
 *
 * `methodOptions` is optional because the gate has no vendor records to hand. The step passes them,
 * so a restored method the vendor no longer offers is caught before pre-flight refuses it.
 */
export const paymentProblems = (
  payment: BulkOrderVendorPayment | undefined,
  totalInclVat: number,
  methodOptions?: BackendPaymentMethodEnum[],
): PaymentProblem[] => {
  const entry = payment ?? emptyVendorPayment();
  const problems: PaymentProblem[] = [];

  if (!entry.method || (methodOptions && !methodOptions.includes(entry.method))) {
    problems.push('method');
  }

  if (entry.deposit.isOn && !isDepositPercentValid(entry.deposit.percent)) {
    problems.push('deposit');
  }

  return problems;
};

/** The standard flow's converter, so a chosen day survives the trip to UTC east of Greenwich. */
const toWireDate = (date: Date | null): string | null =>
  date ? convertToISOWithTimezone(date, false, true) : null;

/**
 * One entry of `paymentInformation`, in the shape `BulkOrderPlacementPaymentDto` takes.
 *
 * 🔴 The deposit leaves as the **amount** step 4 showed, whichever cell it was typed into, and
 * never as its percentage: the figure the officer agreed is the figure requested.
 */
export const toPlacementPayment = (
  vendorId: string,
  payment: BulkOrderVendorPayment,
  totalInclVat: number,
): BulkOrderPlacementPayment => {
  const figures = depositFigures(payment.deposit, totalInclVat);

  return {
    vendorId,
    deposit: figures
      ? {
          amount: figures.amount,
          dueDate: toWireDate(payment.deposit.dueDate),
          assigneeId: payment.deposit.assigneeId,
        }
      : null,
    order: {
      method: payment.method as BackendPaymentMethodEnum,
      dueDate: payment.method === BackendPaymentMethodEnum.COD ? toWireDate(payment.dueDate) : null,
      assigneeId: payment.assigneeId,
    },
  };
};

/** Local midnight today, the floor for both payment dates. */
const startOfToday = (): Date => {
  const today = new Date();

  today.setHours(0, 0, 0, 0);

  return today;
};

/**
 * A stored payment date read back, or `null` where it has since fallen into the past.
 *
 * Context: the same answer `restoreDeliveryDate` gives, for the same reason: the officer lands on
 * step 4 with an empty control rather than on a refusal at placement.
 */
export const restorePaymentDate = (stored: string | null | undefined): Date | null => {
  if (!stored) return null;

  const date = new Date(stored);

  if (Number.isNaN(date.getTime())) return null;

  return date < startOfToday() ? null : date;
};

/** A payment as the session side-car stores it: dates as ISO strings, because `Date` does not survive JSON. */
export interface StoredVendorPayment {
  method: string | null;
  dueDate: string | null;
  assigneeId: string | null;
  deposit: {
    isOn: boolean;
    percent: number | null;
    dueDate: string | null;
    assigneeId: string | null;
  };
}

export const toStoredPayment = (payment: BulkOrderVendorPayment): StoredVendorPayment => ({
  method: payment.method,
  dueDate: payment.dueDate?.toISOString() ?? null,
  assigneeId: payment.assigneeId,
  deposit: { ...payment.deposit, dueDate: payment.deposit.dueDate?.toISOString() ?? null },
});

const isWireMethod = (value: string | null): value is BackendPaymentMethodEnum =>
  value === BackendPaymentMethodEnum.COD || value === BackendPaymentMethodEnum.ACCOUNT;

export const fromStoredPayment = (stored: StoredVendorPayment): BulkOrderVendorPayment => ({
  method: isWireMethod(stored.method) ? stored.method : null,
  dueDate: restorePaymentDate(stored.dueDate),
  assigneeId: stored.assigneeId,
  deposit: { ...stored.deposit, dueDate: restorePaymentDate(stored.deposit.dueDate) },
});
